import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { SubscriptionService } from 'utility/services/subscription'

const Banner = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #fde68a;
    background: #fffbeb;
    color: #78350f;
    margin-bottom: 16px;
    font-size: 13px;
    line-height: 1.5;
    .icon {
        font-size: 20px;
        line-height: 1;
    }
    .body {
        flex: 1;
        min-width: 0;
    }
    strong {
        display: block;
        margin-bottom: 4px;
    }
    .actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 10px;
    }
    code {
        font-size: 12px;
    }
`

const UNPAID = ['PENDING', 'FAILED', 'REQUIRES_PAYMENT_METHOD', 'REQUIRES_ACTION']

/**
 * Latest unpaid payment can be finished with PayNow.
 * A succeeded charge is left alone. History failures hide the banner.
 */
export const PendingPaymentBanner = ({ onRestartUpgrade }) => {
    const [item, setItem] = useState(null)
    const [paying, setPaying] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        let cancelled = false

        async function loadHistory() {
            try {
                const data = await SubscriptionService.getPaymentHistory({
                    pageNumber: 1,
                    pageSize: 1,
                })
                if (cancelled) return
                const row =
                    data?.Items?.[0] ||
                    data?.items?.[0] ||
                    (Array.isArray(data) ? data[0] : null)
                if (!row) return
                const status = String(
                    row.status || row.Status || '',
                ).toUpperCase()
                if (!UNPAID.includes(status)) return
                const canPay = Boolean(
                    row.PaymentOrderId ||
                        row.paymentOrderId ||
                        row.ClientSecretId ||
                        row.clientSecretId,
                )
                if (!canPay) return
                setItem(row)
            } catch {
                // ignore — defensive only
            }
        }

        loadHistory()
        return () => {
            cancelled = true
        }
    }, [])

    if (!item) return null
    const ref =
        item.PaymentOrderId || item.paymentOrderId || item.Id || 'payment'
    const canPayNow = Boolean(
        item.PaymentOrderId || item.paymentOrderId || item.ClientSecretId,
    )

    const handlePayNow = async () => {
        setPaying(true)
        setError('')
        try {
            await SubscriptionService.payNow(item)
            setItem(null)
        } catch (err) {
            const apiErr = err?.error
            setError(
                apiErr?.ErrorMessage ||
                    apiErr?.errorMessage ||
                    apiErr?.message ||
                    apiErr?.Message ||
                    'Pay now did not complete. You can restart the upgrade.',
            )
        } finally {
            setPaying(false)
        }
    }

    return (
        <Banner>
            <span className="icon">⏳</span>
            <div className="body">
                <strong>
                    We didn&apos;t receive confirmation of your last payment
                </strong>
                If your card was charged, contact{' '}
                <a href="mailto:support@lyonsinfo.com">support@lyonsinfo.com</a>{' '}
                with reference <code>{ref}</code>. To try again, restart your
                upgrade.
                {error && <div>{error}</div>}
                <div className="actions">
                    {canPayNow && (
                        <AppPrimaryButton
                            type="button"
                            loading={paying}
                            onClick={handlePayNow}
                        >
                            Pay now
                        </AppPrimaryButton>
                    )}
                    <AppPrimaryButton
                        type="button"
                        onClick={() => onRestartUpgrade?.()}
                        disabled={paying}
                    >
                        Restart upgrade
                    </AppPrimaryButton>
                    <AppSecondaryButton
                        type="button"
                        as="a"
                        href="mailto:support@lyonsinfo.com"
                        onClick={e => {
                            // semantic styled button may not support `as`; fallback
                            e.preventDefault()
                            window.location.href =
                                'mailto:support@lyonsinfo.com'
                        }}
                    >
                        Contact support
                    </AppSecondaryButton>
                </div>
            </div>
        </Banner>
    )
}
